// src/pages/TasksPage.tsx

import { useState, useMemo, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import {
  useGetTasksQuery,
  useUpdateTaskStatusMutation,
  useDeleteTaskMutation,
} from '../api/tasksApi';
import { useGetTaskTemplatesQuery } from '../api/taskTemplatesApi';
import { useGetContactsQuery } from '../api/contactsApi';
import { useGetProjectsQuery } from '../api/projectsApi';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorState from '../components/common/ErrorState';
import Button from '../components/common/Button';
import TaskFormModal from '../components/tasks/TaskFormModal';
import CompletionDialog from '../components/workAccountability/CompletionDialog';
import { useWorkAccountabilityEnabled } from '../hooks/useWorkAccountability';
import { DeleteConfirmDialog } from '../components/ui/ConfirmDialog';
import type { Task, TaskStatus, TaskPriority } from '../types/api';
import { useTaskPermissions } from '../hooks/useTaskPermissions';

const STATUS_CONFIG: Record<TaskStatus, { label: string; color: string; bgColor: string }> = {
  todo: { label: 'To Do', color: 'text-gray-700', bgColor: 'bg-gray-100' },
  in_progress: { label: 'In Progress', color: 'text-blue-700', bgColor: 'bg-blue-100' },
  blocked: { label: 'Blocked', color: 'text-red-700', bgColor: 'bg-red-100' },
  done: { label: 'Done', color: 'text-green-700', bgColor: 'bg-green-100' },
};

const PRIORITY_CONFIG: Record<TaskPriority, { label: string; color: string }> = {
  low: { label: 'Low', color: 'text-gray-500' },
  medium: { label: 'Medium', color: 'text-yellow-600' },
  high: { label: 'High', color: 'text-orange-600' },
  urgent: { label: 'Urgent', color: 'text-red-600' },
};

const STATUSES: TaskStatus[] = ['todo', 'in_progress', 'blocked', 'done'];

type ViewMode = 'list' | 'board';
type GapFilter = 'all' | 'overdue' | 'blocked' | 'unestimated' | 'due_this_week';

// Computed once per page load — good enough for a daily management view
const TODAY_STR = new Date().toISOString().split('T')[0];

function getWeekBounds() {
  const now = new Date();
  const day = now.getDay();
  const diff = day === 0 ? -6 : 1 - day; // adjust to Monday
  const monday = new Date(now);
  monday.setDate(now.getDate() + diff);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    start: monday.toISOString().split('T')[0],
    end: sunday.toISOString().split('T')[0],
  };
}
const WEEK_BOUNDS = getWeekBounds();

export default function TasksPage() {
  const activeWorkspace = useActiveWorkspace();
  const workspaceId = activeWorkspace?.workspace.id;
  const waEnabled = useWorkAccountabilityEnabled();
  const [completionTaskId, setCompletionTaskId] = useState<string | null>(null);

  const [viewMode, setViewMode] = useState<ViewMode>('board');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [contactFilter, setContactFilter] = useState<string>('');
  const [projectFilter, setProjectFilter] = useState<string>('');
  const [gapFilter, setGapFilter] = useState<GapFilter>('all');
  const [actionError, setActionError] = useState<string | null>(null);

  const { search } = useLocation();
  const _params = new URLSearchParams(search);
  const goalFilter = _params.get('goalId');
  const goalFilterName = _params.get('gn');
  const deepLinkTaskId = _params.get('taskId');

  // Deep-link: open task detail modal when ?taskId= is present
  useEffect(() => {
    if (deepLinkTaskId && !selectedTaskId) {
      setSelectedTaskId(deepLinkTaskId);
    }
  }, [deepLinkTaskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    data: tasksResponse,
    isLoading,
    error,
    refetch,
  } = useGetTasksQuery(
    {
      workspaceId,
      relatedContactId: contactFilter || undefined,
      projectId: projectFilter || undefined,
    },
    { skip: !workspaceId }
  );

  // Extract tasks array from response
  const tasks = tasksResponse?.tasks;

  const { data: contactsResponse } = useGetContactsQuery({ workspaceId }, { skip: !workspaceId });
  const contacts = contactsResponse?.contacts;

  const { data: projects } = useGetProjectsQuery({ workspaceId }, { skip: !workspaceId });

  // Fetch active templates count for discoverability
  const { data: templates } = useGetTaskTemplatesQuery(
    { workspaceId, isActive: true },
    { skip: !workspaceId }
  );
  const activeTemplateCount = templates?.length ?? 0;

  const [updateTaskStatus] = useUpdateTaskStatusMutation();
  const [deleteTask] = useDeleteTaskMutation();

  // FU-4 Step 8 — this page rendered New Task, the status selector and
  // Delete to every principal and let the server refuse.
  // `routes/tasks.ts` is gated purely on `requireCapabilityMiddleware`
  // ('tasks', …), so these flags read exactly what it enforces.
  const perms = useTaskPermissions();

  const gapStats = useMemo(() => {
    if (!tasks) return { overdue: 0, blocked: 0, unestimated: 0, dueThisWeek: 0 };
    return {
      overdue: tasks.filter(
        (t) => t.status !== 'done' && !!t.dueDate && t.dueDate.split('T')[0] < TODAY_STR
      ).length,
      blocked: tasks.filter((t) => t.status === 'blocked').length,
      unestimated: tasks.filter((t) => t.status !== 'done' && !t.estimatedMinutes).length,
      dueThisWeek: tasks.filter(
        (t) =>
          t.status !== 'done' &&
          !!t.dueDate &&
          t.dueDate.split('T')[0] >= WEEK_BOUNDS.start &&
          t.dueDate.split('T')[0] <= WEEK_BOUNDS.end
      ).length,
    };
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    const base = goalFilter ? tasks?.filter((t) => t.goalId === goalFilter) : tasks;
    if (!base || gapFilter === 'all') return base;
    switch (gapFilter) {
      case 'overdue':
        return base.filter(
          (t) => t.status !== 'done' && !!t.dueDate && t.dueDate.split('T')[0] < TODAY_STR
        );
      case 'blocked':
        return base.filter((t) => t.status === 'blocked');
      case 'unestimated':
        return base.filter((t) => t.status !== 'done' && !t.estimatedMinutes);
      case 'due_this_week':
        return base.filter(
          (t) =>
            t.status !== 'done' &&
            !!t.dueDate &&
            t.dueDate.split('T')[0] >= WEEK_BOUNDS.start &&
            t.dueDate.split('T')[0] <= WEEK_BOUNDS.end
        );
    }
  }, [tasks, gapFilter, goalFilter]);

  const tasksByStatus = useMemo(() => {
    const grouped: Record<TaskStatus, Task[]> = {
      todo: [],
      in_progress: [],
      blocked: [],
      done: [],
    };
    filteredTasks?.forEach((task) => {
      grouped[task.status]?.push(task);
    });
    return grouped;
  }, [filteredTasks]);

  const isProofRequiredError = (error: unknown): boolean =>
    (error as { data?: { error?: string } } | undefined)?.data?.error === 'PROOF_REQUIRED';

  const handleStatusChange = async (taskId: string, newStatus: TaskStatus) => {
    // Work Accountability: completing (→ done) runs through the completion dialog.
    if (waEnabled && newStatus === 'done') {
      const current = tasks?.find((x) => x.id === taskId);
      if (current && current.status !== 'done') {
        setCompletionTaskId(taskId);
        return;
      }
    }
    try {
      await updateTaskStatus({ id: taskId, status: newStatus }).unwrap();
    } catch (error) {
      if (isProofRequiredError(error)) {
        const t = tasks?.find((x) => x.id === taskId);
        setActionError(`"${t?.title ?? 'This task'}" requires a proof attachment before it can be completed. Open it to add proof.`);
      } else {
        console.error('Failed to update task status:', error);
      }
    }
  };

  const handleDeleteClick = (task: Task) => {
    setTaskToDelete(task);
  };

  const handleConfirmDelete = async () => {
    if (!taskToDelete) return;
    try {
      await deleteTask(taskToDelete.id).unwrap();
      setTaskToDelete(null);
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;

    // Dropped outside a droppable area
    if (!destination) return;

    // Dropped in same position
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // Get the new status from the droppable ID
    const newStatus = destination.droppableId as TaskStatus;

    // Only update if status changed
    if (newStatus !== source.droppableId) {
      // Work Accountability: completing via drag also routes through the dialog.
      if (waEnabled && newStatus === 'done') {
        const current = tasks?.find((x) => x.id === draggableId);
        if (current && current.status !== 'done') {
          setCompletionTaskId(draggableId);
          return;
        }
      }
      try {
        await updateTaskStatus({ id: draggableId, status: newStatus }).unwrap();
      } catch (error) {
        if (isProofRequiredError(error)) {
          const t = tasks?.find((x) => x.id === draggableId);
          setActionError(`"${t?.title ?? 'This task'}" requires a proof attachment before it can be completed. Open it to add proof.`);
        } else {
          console.error('Failed to update task status:', error);
        }
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ErrorState message="Failed to load tasks." onRetry={refetch} />;
  }

  if (!activeWorkspace) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Please select a workspace to view tasks.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Goal filter banner */}
      {goalFilter && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 border border-blue-100 rounded-xl">
          <span className="text-sm text-blue-700">
            Showing tasks for goal{goalFilterName ? `: ${goalFilterName}` : ''}
          </span>
          <Link
            to="/tasks"
            className="ml-auto text-xs text-blue-500 hover:text-blue-700 hover:underline flex-shrink-0"
          >
            Clear filter
          </Link>
        </div>
      )}

      {/* Proof-gating banner (dismissible) */}
      {actionError && (
        <div className="flex items-start gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
          <span className="text-amber-600 text-base leading-none mt-0.5">⚠</span>
          <p className="flex-1 text-sm text-amber-800">{actionError}</p>
          <button
            onClick={() => setActionError(null)}
            className="text-amber-500 hover:text-amber-700 text-sm flex-shrink-0"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
          <p className="text-sm text-gray-500">
            {gapFilter !== 'all'
              ? `${filteredTasks?.length ?? 0} of ${tasks?.length ?? 0}`
              : tasks?.length ?? 0}{' '}
            task{(gapFilter !== 'all' ? filteredTasks?.length : tasks?.length) !== 1 ? 's' : ''}
            <span className="mx-2">·</span>
            <Link to="/recurring-tasks" className="text-blue-600 hover:underline">
              Recurring Tasks{activeTemplateCount > 0 && ` (${activeTemplateCount} active)`}
            </Link>
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Contact Filter */}
          <select
            value={contactFilter}
            onChange={(e) => setContactFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
          >
            <option value="">All Contacts</option>
            {contacts?.map((contact) => (
              <option key={contact.id} value={contact.id}>
                {contact.fullName}
              </option>
            ))}
          </select>
          {/* Project Filter */}
          <select
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
          >
            <option value="">All Projects</option>
            {projects?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {/* View Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              List
            </button>
            <button
              onClick={() => setViewMode('board')}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                viewMode === 'board'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Board
            </button>
          </div>
          {perms.canWriteTasks && (
            <Button onClick={() => setShowCreateModal(true)}>New Task</Button>
          )}
        </div>
      </div>

      {/* Gap Filter Strip */}
      {tasks && tasks.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setGapFilter('all')}
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
              gapFilter === 'all'
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-600 border-gray-300 hover:border-gray-500'
            }`}
          >
            All {gapFilter !== 'all' && `(${tasks.length})`}
          </button>
          {gapStats.overdue > 0 && (
            <button
              onClick={() => setGapFilter(gapFilter === 'overdue' ? 'all' : 'overdue')}
              className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                gapFilter === 'overdue'
                  ? 'bg-red-600 text-white border-red-600'
                  : 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
              }`}
            >
              {gapStats.overdue} overdue
            </button>
          )}
          {gapStats.blocked > 0 && (
            <button
              onClick={() => setGapFilter(gapFilter === 'blocked' ? 'all' : 'blocked')}
              className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                gapFilter === 'blocked'
                  ? 'bg-amber-600 text-white border-amber-600'
                  : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
              }`}
            >
              {gapStats.blocked} blocked
            </button>
          )}
          {gapStats.unestimated > 0 && (
            <button
              onClick={() => setGapFilter(gapFilter === 'unestimated' ? 'all' : 'unestimated')}
              className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                gapFilter === 'unestimated'
                  ? 'bg-gray-700 text-white border-gray-700'
                  : 'bg-gray-50 text-gray-600 border-gray-300 hover:bg-gray-100'
              }`}
            >
              {gapStats.unestimated} unestimated
            </button>
          )}
          {gapStats.dueThisWeek > 0 && (
            <button
              onClick={() => setGapFilter(gapFilter === 'due_this_week' ? 'all' : 'due_this_week')}
              className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                gapFilter === 'due_this_week'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
              }`}
            >
              {gapStats.dueThisWeek} due this week
            </button>
          )}
        </div>
      )}

      {/* Content */}
      {tasks?.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">No Tasks Yet</h3>
          <p className="text-gray-500 mb-4">Create your first task to get started.</p>
          <Button onClick={() => setShowCreateModal(true)}>Create Task</Button>
        </div>
      ) : viewMode === 'board' ? (
        /* Board View with Drag and Drop */
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STATUSES.map((status) => (
              <div key={status} className="bg-gray-50 rounded-xl p-3">
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`font-semibold ${STATUS_CONFIG[status].color}`}>
                    {STATUS_CONFIG[status].label}
                  </h3>
                  <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                    {tasksByStatus[status].length}
                  </span>
                </div>
                <Droppable droppableId={status}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`space-y-2 min-h-[100px] rounded-lg transition-colors ${
                        snapshot.isDraggingOver ? 'bg-blue-50' : ''
                      }`}
                    >
                      {tasksByStatus[status].map((task, index) => (
                        <Draggable key={task.id} draggableId={task.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <TaskCard
                                task={task}
                                onStatusChange={handleStatusChange}
                                onDelete={handleDeleteClick}
                                onClick={() => setSelectedTaskId(task.id)}
                                isDragging={snapshot.isDragging}
                                canWrite={perms.canWriteTasks}
                                canDelete={perms.canDeleteTasks}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            ))}
          </div>
        </DragDropContext>
      ) : (
        /* List View */
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden sm:table-cell">
                    Priority
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden md:table-cell">
                    Due Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden lg:table-cell">
                    Related Contact
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredTasks?.map((task) => (
                  <tr
                    key={task.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedTaskId(task.id)}
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{task.title}</p>
                      {task.description && (
                        <p className="text-xs text-gray-500 truncate max-w-xs">
                          {task.description}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {/* Disabled, not hidden — it displays the current
                          status, which a read-only principal may see. */}
                      <select
                        value={task.status}
                        disabled={!perms.canWriteTasks}
                        aria-label={
                          perms.canWriteTasks
                            ? 'Change status'
                            : 'Status (you cannot change this)'
                        }
                        onChange={(e) => {
                          e.stopPropagation();
                          handleStatusChange(task.id, e.target.value as TaskStatus);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className={`text-xs font-medium px-2 py-1 rounded-full border-0 disabled:opacity-60 disabled:cursor-not-allowed ${STATUS_CONFIG[task.status].bgColor} ${STATUS_CONFIG[task.status].color}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {STATUS_CONFIG[s].label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className={`text-sm font-medium ${PRIORITY_CONFIG[task.priority].color}`}>
                        {PRIORITY_CONFIG[task.priority].label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">
                      {task.dueDate
                        ? new Date(task.dueDate).toLocaleDateString()
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 hidden lg:table-cell">
                      {task.relatedContact?.fullName || '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {perms.canDeleteTasks && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(task);
                          }}
                          className="text-red-600 hover:text-red-700 text-sm"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Task Modal */}
      {workspaceId && (
        <TaskFormModal
          open={showCreateModal}
          taskId={null}
          workspaceId={workspaceId}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Task Detail / Edit Modal */}
      {workspaceId && (
        <TaskFormModal
          open={!!selectedTaskId}
          taskId={selectedTaskId}
          workspaceId={workspaceId}
          onClose={() => setSelectedTaskId(null)}
          onRequestComplete={(id) => {
            // Route completion to the page-level dialog (no modal-inside-a-modal).
            setSelectedTaskId(null);
            setCompletionTaskId(id);
          }}
        />
      )}

      {/* Work Accountability completion dialog (PR-E) — intercepts → done. */}
      {waEnabled && workspaceId && completionTaskId && (() => {
        const t = tasks?.find((x) => x.id === completionTaskId);
        if (!t) return null;
        return (
          <CompletionDialog
            open={!!completionTaskId}
            task={{ id: t.id, workspaceId, assigneeUserId: t.assigneeUserId ?? null, estimatedMinutes: t.estimatedMinutes ?? null, projectId: t.projectId ?? null }}
            onClose={() => setCompletionTaskId(null)}
            onCompleted={() => setCompletionTaskId(null)}
          />
        );
      })()}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        isOpen={!!taskToDelete}
        itemName={taskToDelete?.title}
        itemType="task"
        onConfirm={handleConfirmDelete}
        onCancel={() => setTaskToDelete(null)}
      />
    </div>
  );
}

// Task Card Component for Board View
interface TaskCardProps {
  task: Task;
  onStatusChange: (id: string, status: TaskStatus) => void;
  onDelete: (task: Task) => void;
  onClick?: () => void;
  isDragging?: boolean;
  /**
   * Passed down rather than resolved per card. One hook call per page, not
   * one per row: the answer is identical for every card, and calling it in a
   * list would make a render's cost scale with the task count for no gain.
   */
  canWrite: boolean;
  canDelete: boolean;
}

function TaskCard({ task, onStatusChange, onDelete, onClick, isDragging, canWrite, canDelete }: TaskCardProps) {
  return (
    <div
      className={`bg-white rounded-lg p-3 shadow-sm border border-gray-200 cursor-pointer transition-shadow ${
        isDragging ? 'shadow-lg ring-2 ring-blue-500' : 'hover:shadow-md'
      }`}
      onClick={onClick}
    >
      <p className="font-medium text-gray-900 text-sm mb-2">{task.title}</p>

      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs font-medium ${PRIORITY_CONFIG[task.priority].color}`}>
          {PRIORITY_CONFIG[task.priority].label}
        </span>
        {task.dueDate && (
          <span className="text-xs text-gray-500">
            {new Date(task.dueDate).toLocaleDateString()}
          </span>
        )}
      </div>

      {task.relatedContact && (
        <p className="text-xs text-gray-500 mb-2 truncate">
          Contact: {task.relatedContact.fullName}
        </p>
      )}

      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        {/* DISABLED, not hidden. The selector also DISPLAYS the current
            status, which a read-only principal is entitled to see — hiding
            it would remove information, not just an action. The create and
            delete controls are hidden instead, because they carry no
            information of their own. */}
        <select
          value={task.status}
          disabled={!canWrite}
          aria-label={canWrite ? 'Change status' : 'Status (you cannot change this)'}
          onChange={(e) => {
            e.stopPropagation();
            onStatusChange(task.id, e.target.value as TaskStatus);
          }}
          onClick={(e) => e.stopPropagation()}
          className="text-xs border border-gray-200 rounded px-1 py-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_CONFIG[s].label}
            </option>
          ))}
        </select>
        {canDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(task);
            }}
            className="text-xs text-red-600 hover:text-red-700"
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}
